import { mock } from './fixtures'

// Browser side of the /p/* contract (spec §3.2). Every call goes same-origin;
// the proxy holds the key. `MOCK` serves fixtures so the UI runs before the
// proxy lands: `VITE_PLAY_MOCK=1 pnpm dev`.
import type {
  DemoId,
  PlayConfig,
  PlayError,
  RunRequest,
  RunResponse,
  UploadResult,
} from '@/shared/contract'

export const MOCK = import.meta.env.VITE_PLAY_MOCK === '1'

export class ApiError extends Error {
  constructor(
    public readonly error: PlayError,
    public readonly status: number,
  ) {
    super(error.message)
  }
}

async function json<T>(res: Response): Promise<T> {
  if (res.ok) return (await res.json()) as T
  let err: PlayError = {
    code: `http_${res.status}`,
    message: res.statusText || 'Request failed',
    retryable: res.status >= 500,
  }
  try {
    err = { ...err, ...((await res.json()) as Partial<PlayError>) }
  } catch {
    // not JSON — keep the status-derived error
  }
  throw new ApiError(err, res.status)
}

let configPromise: Promise<PlayConfig> | undefined
export const getConfig = () =>
  (configPromise ??= MOCK ? mock.config() : fetch('/p/config').then((r) => json<PlayConfig>(r)))

export const getSample = (demo: DemoId): Promise<RunResponse> =>
  MOCK ? mock.sample(demo) : fetch(`/p/samples/${demo}`).then((r) => json<RunResponse>(r))

export async function upload(file: File, demo: DemoId, turnstile: string): Promise<UploadResult> {
  if (MOCK) return mock.upload(file)
  const body = new FormData()
  body.set('file', file)
  body.set('demo', demo)
  body.set('turnstile', turnstile)
  return fetch('/p/upload', { method: 'POST', body }).then((r) => json<UploadResult>(r))
}

export const run = (req: RunRequest): Promise<RunResponse> =>
  MOCK
    ? mock.run(req.demo, req.params)
    : fetch('/p/run', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(req),
      }).then((r) => json<RunResponse>(r))

export const getRun = (id: string): Promise<RunResponse> =>
  MOCK
    ? Promise.reject(new Error('mock runs finish inline'))
    : fetch(`/p/runs/${id}?wait=20`).then((r) => json<RunResponse>(r))

/** A run output file (redacted PDF, filled form) streamed through the proxy. */
export const outputUrl = (runId: string, name: string) =>
  `/p/out/${encodeURIComponent(runId)}/${encodeURIComponent(name)}`

export async function compose(
  data: Record<string, unknown>,
  turnstile: string,
): Promise<{ bytes: ArrayBuffer; cost: RunResponse['cost'] }> {
  if (MOCK) return mock.compose()
  const res = await fetch('/p/compose', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ data, turnstile }),
  })
  if (!res.ok) await json(res)
  const cost = res.headers.get('x-play-cost')
  return {
    bytes: await res.arrayBuffer(),
    // ponytail: cost rides a header on the binary reply; falls back to the catalog rate.
    cost: cost ? JSON.parse(cost) : { credits: 1, usd: 0.001, lines: [], estimate: true },
  }
}

export const TERMINAL = new Set(['processed', 'failed', 'canceled', 'cancelled', 'expired'])
export const isTerminal = (status: string) => TERMINAL.has(status)

export async function fetchBytes(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url)
  if (!res.ok)
    throw new ApiError(
      { code: 'fetch_failed', message: `${url}: ${res.status}`, retryable: false },
      res.status,
    )
  return res.arrayBuffer()
}
